import { motion } from 'framer-motion';
import { Lock, Download, RefreshCw, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';

const TARGET_FOLLOWERS = 10000;
const FALLBACK_FOLLOWERS = 10500; // Updated to reflect 10K+ achievement
const COUNTER_API_URL = 'YOUR_GOOGLE_APPS_SCRIPT_URL_HERE';
const REFRESH_INTERVAL = 5 * 60 * 1000;
const BG_VIDEO = 'https://res.cloudinary.com/ddsnj941k/video/upload/Mix_Of_Best_t3wnqv.mp4';

const PRESETS = [
  {
    id: 'zoom',
    name: 'Smooth Zoom',
    description: 'Cinematic zoom transitions with smooth motion blur for dynamic edits.',
    videoSrc: 'https://res.cloudinary.com/ddsnj941k/video/upload/ZOOM_IN_fbsgcj.mp4',
    downloadUrl: 'https://res.cloudinary.com/ddsnj941k/raw/upload/v1781686047/ZOOM_vvhqzu.rar',
    fileName: 'CAYLO_Smooth_Zoom_Preset.rar',
  },
  {
    id: 'text',
    name: 'Text Preset',
    description: 'Stylish text animations that match the CAYLO signature style.',
    videoSrc: 'https://res.cloudinary.com/ddsnj941k/video/upload/TEXT_pgitaz.mp4',
    downloadUrl: 'https://res.cloudinary.com/ddsnj941k/raw/upload/v1781686047/TEXT_zkdirh.rar',
    fileName: 'CAYLO_Text_Preset.rar',
  },
  {
    id: 'shake',
    name: 'Shake Preset',
    description: 'High-energy camera shake effects for hyper edits and beat drops.',
    videoSrc: 'https://res.cloudinary.com/ddsnj941k/video/upload/SHAKE_mvlmn0.mp4',
    downloadUrl: 'https://res.cloudinary.com/ddsnj941k/raw/upload/v1781686047/SHAKE_enyvbs.rar',
    fileName: 'CAYLO_Shake_Preset.rar',
  },
  {
    id: 'cc',
    name: 'CC Preset',
    description: 'Deep cinematic color grading with teal shadows and warm highlights.',
    videoSrc: 'https://res.cloudinary.com/ddsnj941k/video/upload/CC_ipueuc.mp4',
    downloadUrl: 'https://res.cloudinary.com/ddsnj941k/raw/upload/v1781686051/CC_gubyqs.rar',
    fileName: 'CAYLO_CC_Preset.rar',
  },
];

function formatNumber(n: number) {
  return n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K' : n.toString();
}

function AnimatedCount({ value }: { value: number }) {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current === value) return;
    const start = prev.current;
    const end = value;
    const duration = 1200;
    const startTime = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + (end - start) * eased));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    prev.current = value;
  }, [value]);
  return <>{display.toLocaleString()}</>;
}

function PresetCard({ preset, unlocked, index }: { preset: typeof PRESETS[0]; unlocked: boolean; index: number }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) el.play().catch(() => {});
      else el.pause();
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <motion.div
      className="rounded-2xl border overflow-hidden"
      style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(14px)', borderColor: 'rgba(255,255,255,0.08)' }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: 'easeOut' }}
    >
      {/* Video */}
      <div className="relative bg-black group" style={{ aspectRatio: '16/10' }}>
        <video
          ref={videoRef}
          src={preset.videoSrc}
          muted
          loop
          playsInline
          className="w-full h-full"
          style={{ objectFit: 'cover' }}
        />
        {/* gradient overlay with name */}
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-5 pt-12" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)' }}>
          <span className="text-xs tracking-[0.35em] uppercase" style={{ color: '#0be3c5' }}>Preset</span>
          <h3 className="text-white font-black text-2xl leading-tight">{preset.name}</h3>
        </div>
      </div>

      {/* Body */}
      <div className="p-7">
        <p className="text-white/50 text-base mb-7 leading-relaxed">{preset.description}</p>
        {unlocked ? (
          <a
            href={preset.downloadUrl}
            download={preset.fileName}
            className="inline-flex items-center justify-center gap-2 w-full py-4 rounded-xl font-bold text-black text-base transition-all duration-300 hover:opacity-90 hover:scale-[1.02]"
            style={{ background: '#0be3c5', boxShadow: '0 0 24px rgba(11,227,197,0.25)' }}
          >
            <Download size={18} /> Download Free
          </a>
        ) : (
          <div
            className="inline-flex items-center justify-center gap-2 w-full py-4 rounded-xl font-bold text-base cursor-not-allowed select-none"
            style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <Lock size={16} /> Unlocks at 10K Followers
          </div>
        )}
      </div>
    </motion.div>
  );
}

const apiReady = COUNTER_API_URL !== 'YOUR_GOOGLE_APPS_SCRIPT_URL_HERE';

export default function Preset() {
  const [followers, setFollowers] = useState<number | null>(null);
  const [loading, setLoading] = useState(apiReady);
  const [error, setError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [muted, setMuted] = useState(true);
  const bgVideoRef = useRef<HTMLVideoElement>(null);

  const toggleMute = () => {
    const v = bgVideoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const fetchFollowers = async () => {
    if (!apiReady) { setFollowers(FALLBACK_FOLLOWERS); setLoading(false); return; }
    try {
      setError(false);
      const res = await fetch(COUNTER_API_URL);
      const data = await res.json();
      setFollowers(data.followers);
      setLastUpdated(new Date());
    } catch {
      setError(true);
      setFollowers(FALLBACK_FOLLOWERS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFollowers();
    const interval = setInterval(fetchFollowers, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const count = followers ?? 0;
  const isUnlocked = count >= TARGET_FOLLOWERS;

  return (
    <div style={{ position: 'relative', minHeight: '100vh', color: 'white' }}>

      {/* BG video */}
      <div className="fixed inset-0 z-0 bg-black">
        <video
          ref={bgVideoRef}
          autoPlay muted loop playsInline
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        >
          <source src={BG_VIDEO} type="video/mp4" />
        </video>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(1px)' }} />
      </div>

      {/* Sound toggle */}
      <button
        onClick={toggleMute}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all duration-300"
        style={{
          background: 'rgba(0,0,0,0.55)',
          border: `1px solid ${muted ? 'rgba(255,255,255,0.15)' : '#0be3c5'}`,
          color: muted ? 'rgba(255,255,255,0.5)' : '#0be3c5',
          backdropFilter: 'blur(8px)',
        }}
      >
        {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
        {muted ? 'Sound Off' : 'Sound On'}
      </button>

      {/* Content */}
      <div className="relative z-10 px-6 md:px-16 py-28 max-w-6xl mx-auto">

        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <span className="text-xs tracking-[0.4em] uppercase mb-3 block" style={{ color: '#0be3c5' }}>
            🎉 10K MILESTONE REACHED
          </span>
          <h1 className="text-5xl md:text-7xl font-black leading-tight mb-4">
            CLAIM YOUR<br />FREE PRESET PACK
          </h1>
          <p className="text-white/50 text-lg max-w-xl mx-auto">
            We hit 10K followers! As promised, all presets are now unlocked and free to download. Thank you for the amazing support! 🔥
          </p>
        </motion.div>

        {/* Counter */}
        <motion.div
          className="max-w-md mx-auto rounded-2xl border p-6 mb-16 text-center"
          style={{
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(14px)',
            borderColor: isUnlocked ? '#0be3c5' : 'rgba(255,255,255,0.08)',
            boxShadow: isUnlocked ? '0 0 60px rgba(11,227,197,0.2)' : 'none',
            transition: 'all 0.8s ease',
          }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {loading ? (
            <div className="flex items-center justify-center gap-3 py-4">
              <div className="w-5 h-5 rounded-full border-2 border-white/20 border-t-white/60 animate-spin" />
              <span className="text-white/40 text-sm">Loading...</span>
            </div>
          ) : (
            <>
              <div className="mb-1">
                <span className="text-5xl font-black" style={{ color: '#0be3c5' }}>
                  <AnimatedCount value={count} />
                </span>
                <span className="text-white/30 text-xl ml-2">/ {formatNumber(TARGET_FOLLOWERS)}</span>
              </div>
              <p className="text-white/40 text-xs mb-4">TikTok followers</p>
              <div className="w-full h-1.5 rounded-full mb-4 overflow-hidden" style={{ background: 'rgba(255,255,255,0.07)' }}>
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, #0be3c5, #0891b2)' }}
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 1.2, delay: 0.4, ease: 'easeOut' }}
                />
              </div>
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="text-2xl">🎉</div>
                <p className="text-white/80 text-base font-semibold">
                  TARGET ACHIEVED!
                </p>
                <div className="text-2xl">🎉</div>
              </div>
              {lastUpdated && (
                <div className="mt-4 flex items-center justify-center gap-1.5 text-white/20 text-xs">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  Live · {lastUpdated.toLocaleTimeString()}
                  <button onClick={fetchFollowers} className="ml-1 hover:text-white/40 transition-colors"><RefreshCw size={10} /></button>
                </div>
              )}
              {error && <p className="mt-2 text-white/20 text-xs">Showing last known count</p>}
            </>
          )}
        </motion.div>

        {/* 2 per row grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {PRESETS.map((preset, i) => (
            <PresetCard key={preset.id} preset={preset} unlocked={true} index={i} />
          ))}
        </div>

        <p className="text-center text-white/20 text-xs mt-12 tracking-wide">
          🎉 Milestone achieved! All presets permanently unlocked for the community.
        </p>
      </div>
    </div>
  );
}
